(function () {
  'use strict';

  var STORAGE_KEY = 'a11y_plugin_settings';

  var DEFAULTS = {
    contentScale: 100,
    readableFont: false,
    lineHeight: 'unchanged',
    letterSpacing: 'default',
    hideImages: false,
    highContrast: false,
    darkMode: false,
    reduceMotion: false,
    enlargeFocus: false
  };

  var SECTION_KEYS = {
    content: ['reduceMotion', 'contentScale', 'readableFont', 'lineHeight', 'letterSpacing', 'hideImages'],
    navigation: ['enlargeFocus'],
    color: ['highContrast', 'darkMode']
  };

  function AccessibilityPlugin() {
    this.state = this._load();
    this.panel = document.getElementById('a11y-panel');
    this.trigger = document.getElementById('a11y-trigger');
    this.isOpen = false;
    this._previousFocus = null;
    this._applyAll();
    this._bind();
    this._syncUI();
  }

  /* ── Persistence ──────────────────────────────────────── */

  AccessibilityPlugin.prototype._load = function () {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        var merged = {};
        for (var k in DEFAULTS) {
          merged[k] = parsed.hasOwnProperty(k) ? parsed[k] : DEFAULTS[k];
        }
        if (typeof merged.letterSpacing === 'number') merged.letterSpacing = 'default';
        return merged;
      }
    } catch (e) { /* ignore */ }
    return JSON.parse(JSON.stringify(DEFAULTS));
  };

  AccessibilityPlugin.prototype._save = function () {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) { /* quota exceeded or private mode */ }
  };

  /* ── Apply state to DOM ───────────────────────────────── */

  AccessibilityPlugin.prototype._applyAll = function () {
    var html = document.documentElement;

    html.classList.toggle('a11y-readable-font', this.state.readableFont);
    html.classList.remove('a11y-line-height-1-5', 'a11y-line-height-1-75', 'a11y-line-height-2');
    if (this.state.lineHeight === '1.5') html.classList.add('a11y-line-height-1-5');
    if (this.state.lineHeight === '1.75') html.classList.add('a11y-line-height-1-75');
    if (this.state.lineHeight === '2') html.classList.add('a11y-line-height-2');
    html.classList.toggle('a11y-hide-images', this.state.hideImages);
    html.classList.toggle('a11y-reduce-motion', this.state.reduceMotion);
    html.classList.toggle('a11y-enlarge-focus', this.state.enlargeFocus);

    if (this.state.contentScale !== 100) {
      html.style.setProperty('--a11y-content-scale', (this.state.contentScale / 100).toString());
    } else {
      html.style.removeProperty('--a11y-content-scale');
    }

    var spacingMap = { 'default': null, 'increased': '0.05em', 'wcag': '0.12em' };
    var spacingVal = spacingMap[this.state.letterSpacing];
    if (spacingVal) {
      html.style.setProperty('--a11y-letter-spacing', spacingVal);
    } else {
      html.style.removeProperty('--a11y-letter-spacing');
    }

    this._composeFilter();
  };

  AccessibilityPlugin.prototype._composeFilter = function () {
    var html = document.documentElement;
    var parts = [];

    if (this.state.darkMode) parts.push('invert(1) hue-rotate(180deg)');
    if (this.state.highContrast) parts.push('contrast(1.5)');

    html.classList.toggle('a11y-dark-mode', this.state.darkMode);

    if (parts.length) {
      html.style.setProperty('--a11y-filter', parts.join(' '));
    } else {
      html.style.removeProperty('--a11y-filter');
    }
  };

  /* ── Sync UI controls to state ────────────────────────── */

  AccessibilityPlugin.prototype._syncUI = function () {
    var self = this;
    var panel = this.panel;
    if (!panel) return;

    var scaleVal = panel.querySelector('[data-a11y="scale-value"]');
    if (scaleVal) scaleVal.textContent = self.state.contentScale + '%';

    var fontToggle = panel.querySelector('[data-a11y="readable-font"]');
    if (fontToggle) fontToggle.setAttribute('aria-pressed', String(self.state.readableFont));

    var segBtns = panel.querySelectorAll('[data-a11y^="line-height-"]');
    for (var i = 0; i < segBtns.length; i++) {
      var level = segBtns[i].getAttribute('data-a11y').replace('line-height-', '');
      segBtns[i].setAttribute('aria-pressed', String(self.state.lineHeight === level));
    }

    var spacingBtns = panel.querySelectorAll('[data-a11y^="letter-spacing-"]');
    for (var s = 0; s < spacingBtns.length; s++) {
      var spacingLevel = spacingBtns[s].getAttribute('data-a11y').replace('letter-spacing-', '');
      spacingBtns[s].setAttribute('aria-pressed', String(self.state.letterSpacing === spacingLevel));
    }

    var imgToggle = panel.querySelector('[data-a11y="hide-images"]');
    if (imgToggle) imgToggle.setAttribute('aria-pressed', String(self.state.hideImages));

    var contrastBtn = panel.querySelector('[data-a11y="high-contrast"]');
    if (contrastBtn) contrastBtn.setAttribute('aria-pressed', String(self.state.highContrast));

    var darkBtn = panel.querySelector('[data-a11y="dark-mode"]');
    if (darkBtn) darkBtn.setAttribute('aria-pressed', String(self.state.darkMode));

    var motionBtn = panel.querySelector('[data-a11y="reduce-motion"]');
    if (motionBtn) motionBtn.setAttribute('aria-pressed', String(self.state.reduceMotion));

    var focusBtn = panel.querySelector('[data-a11y="enlarge-focus"]');
    if (focusBtn) focusBtn.setAttribute('aria-pressed', String(self.state.enlargeFocus));

    var resetBtn = panel.querySelector('[data-a11y="reset"]');
    if (resetBtn) {
      var changed = JSON.stringify(self.state) !== JSON.stringify(DEFAULTS);
      resetBtn.classList.toggle('visible', changed);
    }

    for (var sec in SECTION_KEYS) {
      var secBtn = panel.querySelector('[data-a11y="reset-' + sec + '"]');
      if (secBtn) {
        var secKeys = SECTION_KEYS[sec];
        var secChanged = false;
        for (var j = 0; j < secKeys.length; j++) {
          if (self.state[secKeys[j]] !== DEFAULTS[secKeys[j]]) { secChanged = true; break; }
        }
        secBtn.classList.toggle('visible', secChanged);
      }
    }
  };

  /* ── Event Binding ────────────────────────────────────── */

  AccessibilityPlugin.prototype._bind = function () {
    var self = this;

    this.trigger.addEventListener('click', function () { self.open(); });

    var skipBtn = document.getElementById('a11y-skip-btn');
    if (skipBtn) skipBtn.addEventListener('click', function () { self.open(); });

    var closeBtn = this.panel.querySelector('[data-a11y="close"]');
    if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen) {
        self.close();
      }
      if (self.isOpen) {
        self._trapFocus(e);
      }
    });

    this.panel.addEventListener('click', function (e) {
      var target = e.target.closest('[data-a11y]');
      if (!target) return;
      var action = target.getAttribute('data-a11y');
      self._handleAction(action, target);
    });

  };

  AccessibilityPlugin.prototype._handleAction = function (action, target) {
    switch (action) {
      case 'scale-down':
        this.state.contentScale = Math.max(90, this.state.contentScale - 5);
        break;
      case 'scale-up':
        this.state.contentScale = Math.min(200, this.state.contentScale + 5);
        break;
      case 'readable-font':
        this.state.readableFont = !this.state.readableFont;
        break;
      case 'line-height-unchanged':
        this.state.lineHeight = 'unchanged';
        break;
      case 'line-height-1.5':
        this.state.lineHeight = '1.5';
        break;
      case 'line-height-1.75':
        this.state.lineHeight = '1.75';
        break;
      case 'line-height-2':
        this.state.lineHeight = '2';
        break;
      case 'letter-spacing-default':
        this.state.letterSpacing = 'default';
        break;
      case 'letter-spacing-increased':
        this.state.letterSpacing = 'increased';
        break;
      case 'letter-spacing-wcag':
        this.state.letterSpacing = 'wcag';
        break;
      case 'hide-images':
        this.state.hideImages = !this.state.hideImages;
        break;
      case 'high-contrast':
        this.state.highContrast = !this.state.highContrast;
        if (this.state.highContrast) this.state.darkMode = false;
        break;
      case 'dark-mode':
        this.state.darkMode = !this.state.darkMode;
        if (this.state.darkMode) this.state.highContrast = false;
        break;
      case 'reduce-motion':
        this.state.reduceMotion = !this.state.reduceMotion;
        break;
      case 'enlarge-focus':
        this.state.enlargeFocus = !this.state.enlargeFocus;
        break;
      case 'reset-content':
      case 'reset-navigation':
      case 'reset-color':
        var section = action.replace('reset-', '');
        var keys = SECTION_KEYS[section];
        if (keys) {
          for (var r = 0; r < keys.length; r++) {
            this.state[keys[r]] = DEFAULTS[keys[r]];
          }
        }
        break;
      case 'reset':
        this.state = JSON.parse(JSON.stringify(DEFAULTS));
        break;
      case 'close':
        this.close();
        return;
      default:
        return;
    }

    this._applyAll();
    this._save();
    this._syncUI();
  };

  /* ── Panel Open / Close ───────────────────────────────── */

  AccessibilityPlugin.prototype.open = function () {
    this._previousFocus = document.activeElement;
    this.panel.classList.add('open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.isOpen = true;

    var self = this;
    var onTransitionEnd = function () {
      self.panel.removeEventListener('transitionend', onTransitionEnd);
      var firstFocusable = self._getVisibleFocusable()[0];
      if (firstFocusable) firstFocusable.focus();
    };
    this.panel.addEventListener('transitionend', onTransitionEnd);
  };

  AccessibilityPlugin.prototype.close = function () {
    this.panel.classList.remove('open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.isOpen = false;

    var mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      mainContent.removeAttribute('tabindex');
    } else if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
      this._previousFocus.focus();
    }
  };

  /* ── Focus Trap ───────────────────────────────────────── */

  AccessibilityPlugin.prototype._getVisibleFocusable = function () {
    var all = this.panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    var visible = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].offsetParent !== null || all[i].offsetWidth > 0 || all[i].offsetHeight > 0) {
        visible.push(all[i]);
      }
    }
    return visible;
  };

  AccessibilityPlugin.prototype._trapFocus = function (e) {
    if (e.key !== 'Tab') return;

    var focusable = this._getVisibleFocusable();
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (!this.panel.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  /* ── Init ─────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      new AccessibilityPlugin();
    });
  } else {
    new AccessibilityPlugin();
  }
})();

/* ==================================================================
   Accessibility Plugin Portable Bundle Extensions (v4.7.0)
   Moved from layout/theme.liquid + assets/scripts.js
   ================================================================== */

/* Cart screen-reader announcer (vanilla) */
(function () {
  /**
   * Plain text for aria-live: Shopify.formatMoney() and some product titles include
   * HTML (e.g. span.money); strip tags so screen readers do not announce markup.
   */
  window.plainTextForAnnouncement = function (value) {
    if (value == null) return '';
    var s = String(value);
    if (!/</.test(s)) return s.trim();
    var d = document.createElement('div');
    d.innerHTML = s;
    return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
  };

  /**
   * Screen reader announcement after /cart/add.js (and multi-add) succeeds.
   * Skips full-cart payloads from /cart/change.js (those include item_count + items).
   * Optional onComplete runs after the live region updates (e.g. before checkout redirect).
   */
  window.announceCartAddSuccess = function (addResponse, onComplete) {
    if (!addResponse) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }
    if (
      typeof addResponse.item_count === 'number' &&
      Array.isArray(addResponse.items)
    ) {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    var lineItems = [];
    if (Array.isArray(addResponse.items) && addResponse.items.length) {
      lineItems = addResponse.items;
    } else if (addResponse.title) {
      lineItems = [addResponse];
    } else {
      if (typeof onComplete === 'function') onComplete();
      return;
    }

    function formatMoneyCents(cents) {
      var n = typeof cents === 'number' ? cents : parseInt(cents, 10);
      if (isNaN(n)) return '';
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        var fmt =
          (window.theme && window.theme.moneyWithCurrencyFormat) ||
          (window.theme && window.theme.moneyFormat);
        if (fmt) {
          return window.plainTextForAnnouncement(Shopify.formatMoney(n, fmt));
        }
      }
      return (n / 100).toFixed(2);
    }

    function linePart(li) {
      var name = window.plainTextForAnnouncement(li.title || li.product_title || 'Item');
      var q = li.quantity != null ? li.quantity : 1;
      return name + ' ' + q;
    }

    var namesQty = lineItems.map(linePart).join(', ');
    var itemTotalCents = lineItems.reduce(function (sum, li) {
      var fp = li.final_line_price != null ? li.final_line_price : li.line_price;
      if (fp == null && li.price != null && li.quantity != null) {
        fp = li.price * li.quantity;
      }
      return sum + (fp || 0);
    }, 0);

    function postMessage(msg) {
      var el = document.getElementById('cart-status-message');
      if (!el) {
        if (typeof onComplete === 'function') onComplete();
        return;
      }
      el.textContent = '';
      requestAnimationFrame(function () {
        el.textContent = msg;
        if (typeof onComplete === 'function') {
          setTimeout(onComplete, 150);
        }
      });
    }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var cartTotal = formatMoneyCents(cart.total_price);
        var itemTotal = formatMoneyCents(itemTotalCents);
        var msg =
          namesQty +
          ' added to cart. Item total: ' +
          itemTotal +
          ', Cart total: ' +
          cartTotal;
        postMessage(msg);
      })
      .catch(function () {
        var itemTotal = formatMoneyCents(itemTotalCents);
        var msg =
          namesQty +
          ' added to cart. Item total: ' +
          itemTotal +
          ', Cart total: unavailable';
        postMessage(msg);
      });
  };
})();

/* Skip-to-cart click wiring (vanilla) */
(function () {
  function bindSkipToCart() {
    var btn = document.getElementById('skip-to-cart');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var cart = document.querySelector('.side-cart');
      if (cart) {
        cart.classList.add('active');
        var closeBtn = cart.querySelector('.close-cart');
        if (closeBtn) closeBtn.focus();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindSkipToCart);
  } else {
    bindSkipToCart();
  }
})();

/* Slick/sticky/next-tab accessibility helpers (jQuery + Slick) */
(function () {
  if (typeof window.jQuery === 'undefined') {
    try { console.warn('[AccessibilityPlugin] jQuery not found; skipping Slick/sticky accessibility helpers.'); } catch (e) {}
    return;
  }

  (function ($) {
    /**
     * Slick default DOM order: prev -> .slick-list -> next, so Tab visits the whole track before "next".
     * Custom prevArrow/nextArrow markup only gets .slick-arrow (not .slick-prev / .slick-next), so we use
     * direct-child buttons: first .slick-arrow = prev, second = next.
     * Move next before .slick-list so focus order is: prev -> next -> slide content.
     */
    function reorderCategoriesSlideArrows($slider) {
      if (!$slider || !$slider.length) return;
      if (!$slider.hasClass('categories_slide')) return;

      var $list = $slider.children('.slick-list').first();
      if (!$list.length) return;

      var $arrows = $slider.children('button.slick-arrow');
      if ($arrows.length < 2) return;

      var $nextArrow = $arrows.last();
      $nextArrow.insertBefore($list);
    }

    function scheduleReorderCategoriesSlideArrows($slider) {
      if (!$slider || !$slider.length) return;
      var run = function () {
        reorderCategoriesSlideArrows($slider);
      };
      run();
      setTimeout(run, 0);
      setTimeout(run, 50);
      setTimeout(run, 250);
    }

    /**
     * Same as categories: Slick default is prev -> .slick-list -> next, so Tab walks every slide before arrows.
     * Move "next" before .slick-list so order is prev -> next -> track (Latest terpenes / #newestTerpenes).
     */
    function reorderNewestTerpenesSlideArrows($slider) {
      if (!$slider || !$slider.length) return;
      var ntEl = document.getElementById('newestTerpenes');
      if (!ntEl || !ntEl.contains($slider[0])) return;

      // Use find(), not children(): arrows are not always direct children of .slick-slider after Slick inits.
      var $list = $slider.find('.slick-list').first();
      if (!$list.length) return;

      var $arrows = $slider.find('button.slick-arrow');
      if ($arrows.length < 2) return;

      $arrows.last().insertBefore($list);
    }

    /**
     * Slick appends .slick-dots after .slick-list. Use find() - .slick-dots is often not a direct child (wrapper divs).
     * Target order: prev -> next -> dots -> .slick-list so Tab reaches the product card after the last dot.
     */
    function reorderNewestTerpenesDotsBeforeList($slider) {
      if (!$slider || !$slider.length) return;
      var ntEl = document.getElementById('newestTerpenes');
      if (!ntEl || !ntEl.contains($slider[0])) return;

      var $list = $slider.find('.slick-list').first();
      var $dots = $slider.find('.slick-dots').first();
      if (!$list.length || !$dots.length) return;

      $dots.insertBefore($list);
    }

    function scheduleReorderNewestTerpenesArrows($slider) {
      if (!$slider || !$slider.length) return;
      var run = function () {
        reorderNewestTerpenesSlideArrows($slider);
        reorderNewestTerpenesDotsBeforeList($slider);
      };
      run();
      setTimeout(run, 0);
      setTimeout(run, 50);
      setTimeout(run, 250);
      setTimeout(run, 500);
      window.requestAnimationFrame(function () {
        run();
        window.requestAnimationFrame(run);
      });
    }

    /** Slick rebuilds on init / breakpoint / setPosition - reorder must run again or DOM snaps back to list -> dots. */
    function bindNewestTerpenesCarouselDomReorder($slideRoot) {
      var ntEl = document.getElementById('newestTerpenes');
      if (!ntEl || !ntEl.contains($slideRoot[0])) return;
      $slideRoot
        .off('init.ntReorder reInit.ntReorder breakpoint.ntReorder setPosition.ntReorder')
        .on('init.ntReorder reInit.ntReorder breakpoint.ntReorder setPosition.ntReorder', function () {
          scheduleReorderNewestTerpenesArrows($(this));
        });
    }
    window.abstraxBindNewestTerpenesCarouselDomReorder = bindNewestTerpenesCarouselDomReorder;

    function fixSlickAccessibility() {
      $('.slick-initialized').each(function() {
        var $slider = $(this);

        if ($slider.attr('role') === 'toolbar') {
          $slider.attr('role', 'region');
        }
        if (!$slider.attr('aria-label')) {
          $slider.attr('aria-label', 'Content carousel');
        }

        scheduleReorderCategoriesSlideArrows($slider);
        scheduleReorderNewestTerpenesArrows($slider);
        bindNewestTerpenesCarouselDomReorder($slider);

        function updateSlideVisibility() {
          var ntEl = document.getElementById('newestTerpenes');
          var isNewestTerpenes = !!(ntEl && $slider[0] && ntEl.contains($slider[0]));
          // Latest terpenes: include .slick-cloned slides. The centered tile can be a clone when
          // infinite is on at smaller breakpoints; excluding clones made isCurrent false for every
          // .slick-slide so ALL focusables (inside .item) stayed tabindex="-1".
          var $slides = $slider.find('.slick-slide');
          var $activeSlides = $slides.filter('.slick-active');
          // Slick + centerMode often sets aria-hidden="true" on the centered slide; aria-hidden alone is unreliable.
          var useSlickActive = $activeSlides.length > 0;

          $slides.each(function () {
            var $slide = $(this);
            var isCurrent;
            if (isNewestTerpenes) {
              // Class-based - do not rely on node identity vs a pre-filtered jQuery set (breaks with clones / class timing).
              if ($slider.find('.slick-slide.slick-center').length) {
                isCurrent = $slide.hasClass('slick-center');
              } else if ($slider.find('.slick-slide.slick-current').length) {
                isCurrent = $slide.hasClass('slick-current');
              } else {
                var $firstActive = $slider.find('.slick-slide.slick-active').first();
                isCurrent =
                  $firstActive.length && $slide[0] === $firstActive[0];
              }
            } else if ($slider.hasClass('categories_slide')) {
              // Match theme.liquid (.native / .premimu): those handlers use .slick-current for the active
              // tile. We previously preferred .slick-center whenever it existed; in centerMode Slick can
              // leave .slick-center on the wrong node while .slick-current tracks the real index - only
              // slide 0 then stayed "current" for tabindex/aria-hidden (Signature / .jack-new-bac).
              var hasSlickCenter = $slider.find('.slick-slide.slick-center').length > 0;
              if (!hasSlickCenter && useSlickActive) {
                // e.g. slidesToShow: 2 @ 767px - no .slick-center; every visible slide is .slick-active.
                isCurrent = $slide.hasClass('slick-active');
              } else if ($slider.find('.slick-slide.slick-current').length) {
                isCurrent = $slide.hasClass('slick-current');
              } else if (hasSlickCenter) {
                isCurrent = $slide.hasClass('slick-center');
              } else if (useSlickActive) {
                isCurrent = $slide.hasClass('slick-active');
              } else {
                isCurrent = $slide.attr('aria-hidden') !== 'true';
              }
            } else {
              isCurrent = useSlickActive
                ? $slide.hasClass('slick-active')
                : $slide.attr('aria-hidden') !== 'true';
            }

            if (!isCurrent) {
              $slide
                .find(
                  'a, button, input, select, textarea, iframe, area[href], summary, [tabindex], [role="button"], [contenteditable="true"]'
                )
                .add($slide.find('.qty'))
                .attr('tabindex', '-1');
            } else {
              var $activeFocusables = $slide.find(
                'a, button, input, select, textarea, iframe, area[href], summary, [tabindex], [role="button"], [contenteditable="true"]'
              );
              // .categories_slide: .qty spans are tabindex="-1" when inactive but are not matched by
              // the selector above; without clearing/restoring them, Tab can never reach the rest of the
              // card (title link, form) after the image link.
              if ($slider.hasClass('categories_slide')) {
                $activeFocusables = $activeFocusables.add($slide.find('.qty'));
              }
              $activeFocusables.each(function () {
                var $el = $(this);
                var tag = this.nodeName.toLowerCase();
                // Homepage "Latest terpenes" (#newestTerpenes): keyboard order is slider -> variant -> qty -> ATC (not title links first).
                if (tag === 'select' && $el.attr('name') === 'id') {
                  // Keep variant <select> in tab order on carousels where it is the real size control
                  // (category botanical rows). Elsewhere it stays -1 when a custom UI drives variants.
                  if (isNewestTerpenes || $slider.hasClass('categories_slide')) {
                    $el.removeAttr('tabindex');
                  } else {
                    $el.attr('tabindex', '-1');
                  }
                } else {
                  $el.removeAttr('tabindex');
                }
              });
              if ($slider.hasClass('categories_slide') && isCurrent) {
                $slide.find('.qty').attr('tabindex', '0');
                // Match #newestTerpenes: explicit tab order for ATC + quantity input (native <a> may
                // still need 0 with javascript: href; theme may have altered inputs on other pages).
                $slide.find('.add_to_cart_buttona').attr('tabindex', '0');
                $slide.find('input.quantity-selector2').removeAttr('tabindex');
              }
              if (isNewestTerpenes) {
                $slide.find('a').attr('tabindex', '-1');
                $slide.find('.p-des').attr('tabindex', '-1');
                $slide.find('.tol-abx[role="button"]').attr('tabindex', '-1');
                $slide.find('select[name="id"]').removeAttr('tabindex');
                $slide.find('.qty').attr('tabindex', '0');
                $slide.find('input.quantity-selector2').removeAttr('tabindex');
                $slide.find('.add_to_cart_buttona').attr('tabindex', '0');
              }
            }

            // #newestTerpenes slides are excluded from theme.liquid's global
            // $('.slick-slide').not(...#newestTerpenes).attr('aria-hidden','false'), so Slick can leave
            // aria-hidden="true" on the center slide - browsers then omit focusables from the tab order.
            if (isNewestTerpenes) {
              $slide.attr('aria-hidden', isCurrent ? 'false' : 'true');
              // Theme / Slick sometimes set tabindex="0" on .slick-slide - the wrapper then
              // receives Tab before inner controls, so focus skips the product form entirely.
              if (isCurrent) {
                $slide.removeAttr('tabindex');
              } else {
                $slide.attr('tabindex', '-1');
              }
            } else if ($slider.hasClass('categories_slide')) {
              // Slick + centerMode can leave aria-hidden wrong on the visible slide; inactive slides
              // stay out of the tab order via tabindex="-1" on focusables (and slide wrapper below).
              if (isCurrent) {
                $slide.attr('aria-hidden', 'false');
                $slide.removeAttr('tabindex');
              } else {
                $slide.attr('aria-hidden', 'true');
                $slide.attr('tabindex', '-1');
              }
            }
          });

          if (isNewestTerpenes) {
            // Do not blanket-hide .slick-cloned here - centerMode + infinite can put
            // .slick-center on a clone; that would force aria-hidden="true" on the visible card
            // after the per-slide loop sets it false, breaking Tab into .item.
            $slider.children('.slick-list').removeAttr('aria-hidden');
            $('#newestTerpenes .slick-arrow').attr('tabindex', '0');
          } else if ($slider.hasClass('categories_slide')) {
            $slider.children('.slick-list').removeAttr('aria-hidden');
          }
        }

        updateSlideVisibility();
        $slider
          .off('afterChange.slickA11y breakpoint.slickA11y reInit.slickA11y setPosition.slickA11y')
          .on('afterChange.slickA11y breakpoint.slickA11y reInit.slickA11y setPosition.slickA11y', updateSlideVisibility);

        // Re-implement Left/Right arrow-key slide navigation for #newestTerpenes.
        // We set accessibility: false on that slider to stop Slick's built-in aria-hidden
        // management from overriding fixSlickAccessibility, but that also removes Slick's
        // keydown handler. Restore it here with the same form-field guard Slick uses.
        (function () {
          var ntEl = document.getElementById('newestTerpenes');
          if (!ntEl || !$slider[0] || !ntEl.contains($slider[0])) return;
          // Make the slider itself focusable so arrow keys work when tabbing to it directly.
          $slider.attr('tabindex', '0');
          $slider.off('keydown.ntKbd').on('keydown.ntKbd', function (e) {
            // Don't override native behaviour inside form controls.
            if ((e.target.tagName || '').match(/TEXTAREA|INPUT|SELECT/i)) return;
            if (e.key === 'ArrowLeft' || e.keyCode === 37) {
              e.preventDefault();
              $slider.slick('slickPrev');
            } else if (e.key === 'ArrowRight' || e.keyCode === 39) {
              e.preventDefault();
              $slider.slick('slickNext');
            }
          });
        })();
      });
    }

    // Sticky PDP bar duplicates main product controls; exclude from sequential Tab (mouse/touch unchanged).
    // theme.liquid deferred scripts re-assign tabindex - re-run after those timeouts.
    $(function () {
      function excludeStickyCartFromTabOrder() {
        $('.sticky_cart')
          .find('a[href], button, input:not([type="hidden"]), select, textarea')
          .attr('tabindex', '-1');
        $('.sticky_cart .qty-flex .qty').attr('tabindex', '-1');
      }
      excludeStickyCartFromTabOrder();
      setTimeout(excludeStickyCartFromTabOrder, 0);
      setTimeout(excludeStickyCartFromTabOrder, 500);
      setTimeout(excludeStickyCartFromTabOrder, 3100);
    });

    /** Latest terpenes (#newestTerpenes): Tab on "next" advances slide and moves focus to variant select. */
    $(document).on('keydown', '#newestTerpenes .slick-next', function (e) {
      if (e.key !== 'Tab' || e.shiftKey) return;
      e.preventDefault();
      var $slider = $('#newestTerpenes .a-what-new-slide').first();
      if (!$slider.length || !$slider.hasClass('slick-initialized')) return;
      $slider.slick('slickNext');
      setTimeout(function () {
        var $curSlide = $slider.find('.slick-slide.slick-center').first();
        if (!$curSlide.length) {
          $curSlide = $slider.find('.slick-slide.slick-current').first();
        }
        var $target = $curSlide.find('select[name="id"]').first();
        if ($target.length) {
          $target.focus();
        }
      }, 250);
    });

    setTimeout(fixSlickAccessibility, 500);
    $(window).on('load', function() {
      setTimeout(fixSlickAccessibility, 1000);
    });

    window.abstraxFixSlickAccessibility = fixSlickAccessibility;
    fixSlickAccessibility();
  })(window.jQuery);
})();

/* Global keyboard shortcuts registry (jQuery) */
(function () {
  if (typeof window.jQuery === 'undefined') {
    try { console.warn('[AccessibilityPlugin] jQuery not found; skipping keyboard shortcut module.'); } catch (e) {}
    return;
  }

  (function ($) {
    var shortcuts = [];
    var hintDismissStorageKey = 'abstraxShortcutsHintDismissed';
    var altPeekStorageKey = 'abstraxShortcutsAltPeekState';
    var hintTabCount = 0;
    var hintShown = false;
    var hintDismissed = false;
    var hintBubble = null;
    var hintAnnouncer = null;
    var hintList = null;
    var hintCountdownEl = null;
    var hintCountdownProgress = null;
    var hintCountdownSeconds = null;
    var hintCountdownTotalMs = 20000;
    var hintCountdownStartedAt = 0;
    var hintCountdownElapsedAtPause = 0;
    var hintCountdownIsPaused = false;
    var hintCountdownTickId = null;
    var hintCountdownTimeoutId = null;
    var hintAnnounceTimerId = null;
    var hintAnnounceClearTimerId = null;
    var peekBubble = null;
    var peekList = null;
    var peekHeader = null;
    var peekShowTimer = null;
    var peekVisible = false;
    var altPeekBubble = null;
    var altPeekBody = null;
    var altPeekShowTimer = null;
    var altPeekVisible = false;
    // True when the hint bubble was shown because the user pressed Tab ≥4 times (keyboard-nav flow).
    // Used to decide whether to surface the alt-only peek after the hint is dismissed.
    var hintTriggeredByKeyboardNav = false;

    /** jQuery 1.x keydown does not copy KeyboardEvent.code onto jQuery.Event - use the native event. */
    function nativeKeyboardEvent(jqEvent) {
      return jqEvent && jqEvent.originalEvent ? jqEvent.originalEvent : jqEvent;
    }

    function shortcutsDebugEnabled() {
      try {
        if (window.AbstraxShortcutsDebug === true) return true;
        if (typeof localStorage !== 'undefined' && localStorage.getItem('abstraxShortcutsDebug') === '1') return true;
      } catch (e) {
        /* ignore */
      }
      return false;
    }

    function debugLog() {
      if (!shortcutsDebugEnabled()) return;
      try {
        console.info.apply(console, ['[AbstraxShortcuts]'].concat([].slice.call(arguments)));
      } catch (e) {
        /* ignore */
      }
    }

    function isMacPlatform() {
      var platform = '';
      try {
        if (navigator.userAgentData && navigator.userAgentData.platform) {
          platform = navigator.userAgentData.platform;
        } else {
          platform = navigator.platform || navigator.userAgent || '';
        }
      } catch (e) {
        platform = navigator.platform || '';
      }
      return /Mac|iPhone|iPad|iPod/i.test(platform);
    }

    function formatShortcutLabel(label) {
      if (!label) return '';
      if (!isMacPlatform()) return label;
      return label.replace(/^Alt\+/, 'Option+');
    }

    function syncShortcutsHintCountdownLabels() {
      if (!hintBubble) return;
      var mod = isMacPlatform() ? 'Option' : 'Alt';
      var pauseKey = hintBubble.querySelector('.a11y-shortcuts-hint-countdown-pause-key');
      if (pauseKey) {
        pauseKey.textContent = mod;
      }
    }

    function clearShortcutsHintAnnouncement() {
      if (hintAnnounceTimerId) {
        clearTimeout(hintAnnounceTimerId);
        hintAnnounceTimerId = null;
      }
      if (hintAnnounceClearTimerId) {
        clearTimeout(hintAnnounceClearTimerId);
        hintAnnounceClearTimerId = null;
      }
      if (hintAnnouncer) {
        hintAnnouncer.textContent = '';
      }
    }

    function announceShortcutsHint() {
      if (!hintAnnouncer) return;
      clearShortcutsHintAnnouncement();
      var mod = isMacPlatform() ? 'Option' : 'Alt';
      hintAnnounceTimerId = setTimeout(function () {
        hintAnnounceTimerId = null;
        if (!hintShown || !hintAnnouncer) return;
        hintAnnouncer.textContent = 'Keyboard shortcuts available. Open the Keyboard shortcuts region for the full list, or press ' + mod + ' plus Shift plus X or Escape to dismiss.';
        hintAnnounceClearTimerId = setTimeout(function () {
          hintAnnounceClearTimerId = null;
          if (hintAnnouncer) {
            hintAnnouncer.textContent = '';
          }
        }, 1500);
      }, 50);
    }

    function escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function renderShortcutChord(label) {
      var formatted = formatShortcutLabel(label);
      var parts = String(formatted || '').split('+').filter(function (part) {
        return String(part).trim().length > 0;
      });
      if (!parts.length) return '';
      if (parts.length === 1) {
        return '<kbd>' + escapeHtml(parts[0]) + '</kbd>';
      }

      return '<span class="a11y-shortcuts-hint-chord">' + parts.map(function (part, index) {
        var keyHtml = '<kbd>' + escapeHtml(String(part).trim()) + '</kbd>';
        if (index === 0) return keyHtml;
        return '<span class="a11y-shortcuts-hint-plus" aria-hidden="true">+</span>' + keyHtml;
      }).join('') + '</span>';
    }

    function renderShortcutsHintList() {
      if (!hintList || !window.AbstraxShortcuts || typeof window.AbstraxShortcuts.list !== 'function') return;
      var entries = window.AbstraxShortcuts.list().filter(function (shortcut) {
        return shortcut && shortcut.keyLabel && shortcut.description;
      });
      hintList.innerHTML = entries.map(function (shortcut) {
        return '<li>' + renderShortcutChord(shortcut.keyLabel) + '<span>' + escapeHtml(shortcut.description) + '</span></li>';
      }).join('');
    }

    function renderShortcutsPeekList() {
      if (!peekList || !window.AbstraxShortcuts || typeof window.AbstraxShortcuts.list !== 'function') return;
      var entries = window.AbstraxShortcuts.list().filter(function (shortcut) {
        return shortcut && shortcut.triggerKey && shortcut.id !== 'close-shortcuts-hint';
      });
      if (!entries.length) {
        peekList.innerHTML = '';
        return;
      }
      peekList.innerHTML = entries.map(function (shortcut) {
        var keyText = escapeHtml(String(shortcut.triggerKey || '').toUpperCase());
        var labelText = escapeHtml(shortcut.shortLabel || shortcut.description || '');
        return '<li><kbd>' + keyText + '</kbd><span>' + labelText + '</span></li>';
      }).join('');
      if (peekHeader) {
        peekHeader.textContent = isMacPlatform() ? 'Option+Shift' : 'Alt+Shift';
      }
    }

    function clearHintCountdownTimers() {
      if (hintCountdownTickId) {
        clearInterval(hintCountdownTickId);
        hintCountdownTickId = null;
      }
      if (hintCountdownTimeoutId) {
        clearTimeout(hintCountdownTimeoutId);
        hintCountdownTimeoutId = null;
      }
    }

    function updateHintCountdownDisplay(remainingMs) {
      if (!hintCountdownSeconds) return;
      var secondsLeft = Math.ceil(Math.max(remainingMs, 0) / 1000);
      hintCountdownSeconds.textContent = String(secondsLeft);
    }

    function applyHintCountdownAnimation(elapsedMs) {
      if (!hintCountdownProgress) return;
      hintCountdownProgress.style.animation = 'none';
      void hintCountdownProgress.offsetHeight;
      hintCountdownProgress.style.animation = 'a11yShortcutsCountdown 20s linear forwards';
      hintCountdownProgress.style.animationDelay = '-' + (Math.max(elapsedMs, 0) / 1000) + 's';
      /* Inline shorthand wins over stylesheet; pause must set animationPlayState on the element. */
      hintCountdownProgress.style.animationPlayState = 'running';
    }

    function clearShortcutsHintCountdown() {
      clearHintCountdownTimers();
      hintCountdownStartedAt = 0;
      hintCountdownElapsedAtPause = 0;
      hintCountdownIsPaused = false;
      if (hintCountdownEl) {
        hintCountdownEl.classList.remove('is-paused');
      }
      if (hintCountdownProgress) {
        hintCountdownProgress.style.animation = '';
        hintCountdownProgress.style.animationDelay = '';
        hintCountdownProgress.style.animationPlayState = '';
      }
      updateHintCountdownDisplay(hintCountdownTotalMs);
    }

    function runShortcutsHintCountdown(remainingMs) {
      clearHintCountdownTimers();
      if (remainingMs <= 0) {
        dismissShortcutsHint();
        return;
      }

      hintCountdownStartedAt = Date.now();
      updateHintCountdownDisplay(remainingMs);
      hintCountdownTickId = setInterval(function () {
        if (hintCountdownIsPaused || !hintShown) return;
        var elapsedSinceResume = Date.now() - hintCountdownStartedAt;
        var remaining = remainingMs - elapsedSinceResume;
        updateHintCountdownDisplay(remaining);
      }, 250);
      hintCountdownTimeoutId = setTimeout(function () {
        dismissShortcutsHint();
      }, remainingMs);
    }

    function startShortcutsHintCountdown() {
      if (!hintShown) return;
      hintCountdownElapsedAtPause = 0;
      hintCountdownIsPaused = false;
      if (hintCountdownEl) {
        hintCountdownEl.classList.remove('is-paused');
      }
      applyHintCountdownAnimation(0);
      runShortcutsHintCountdown(hintCountdownTotalMs);
    }

    function pauseShortcutsHintCountdown() {
      if (!hintShown || hintCountdownIsPaused) return;
      hintCountdownIsPaused = true;
      hintCountdownElapsedAtPause += Date.now() - hintCountdownStartedAt;
      if (hintCountdownEl) {
        hintCountdownEl.classList.add('is-paused');
      }
      if (hintCountdownProgress) {
        hintCountdownProgress.style.animationPlayState = 'paused';
      }
      clearHintCountdownTimers();
    }

    function resumeShortcutsHintCountdown() {
      if (!hintShown || !hintCountdownIsPaused) return;
      hintCountdownIsPaused = false;
      if (hintCountdownEl) {
        hintCountdownEl.classList.remove('is-paused');
      }
      /* Do not restart the keyframe animation here: rebuilding with a negative delay can jump
         relative to the dashoffset frozen at pause. CSS pause/resume keeps the ring continuous. */
      if (hintCountdownProgress) {
        hintCountdownProgress.style.animationPlayState = 'running';
      }
      runShortcutsHintCountdown(hintCountdownTotalMs - hintCountdownElapsedAtPause);
    }

    function clearPeekTimer() {
      if (peekShowTimer) {
        clearTimeout(peekShowTimer);
        peekShowTimer = null;
      }
    }

    function clearAltPeekTimer() {
      if (altPeekShowTimer) {
        clearTimeout(altPeekShowTimer);
        altPeekShowTimer = null;
      }
    }

    function getAltPeekSessionState() {
      try {
        return sessionStorage.getItem(altPeekStorageKey) || '';
      } catch (e) {
        /* ignore */
      }
      return '';
    }

    function setAltPeekSessionState(nextState) {
      try {
        sessionStorage.setItem(altPeekStorageKey, nextState);
      } catch (e) {
        /* ignore */
      }
    }

    function setAltPeekPersistFlag() {
      if (getAltPeekSessionState() === 'dismissed') return;
      setAltPeekSessionState('persist');
    }

    function setAltPeekDismissedFlag() {
      setAltPeekSessionState('dismissed');
    }

    function hideShortcutsAltPeek() {
      clearAltPeekTimer();
      if (!altPeekBubble) return;
      altPeekBubble.classList.remove('is-visible');
      altPeekVisible = false;
      setTimeout(function () {
        if (!altPeekVisible && altPeekBubble) {
          altPeekBubble.hidden = true;
        }
      }, 140);
    }

    function showShortcutsAltPeek() {
      if (!altPeekBubble || altPeekVisible) return;
      if (isEditableElement(document.activeElement)) return;
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (!altPeekBody) return;
      altPeekBody.innerHTML = renderShortcutChord('Alt+Shift') + ' for shortcuts';
      altPeekBubble.hidden = false;
      void altPeekBubble.offsetHeight;
      altPeekBubble.classList.add('is-visible');
      altPeekVisible = true;
    }

    function hideShortcutsPeek() {
      clearPeekTimer();
      if (!peekBubble) return;
      peekBubble.classList.remove('is-visible');
      peekVisible = false;
      setTimeout(function () {
        if (!peekVisible && peekBubble) {
          peekBubble.hidden = true;
        }
      }, 140);
    }

    function showShortcutsPeek() {
      if (!peekBubble || peekVisible) return;
      clearAltPeekTimer();
      hideShortcutsAltPeek();
      if (isEditableElement(document.activeElement)) return;
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      renderShortcutsPeekList();
      if (!peekList || !peekList.children.length) return;
      peekBubble.hidden = false;
      void peekBubble.offsetHeight;
      peekBubble.classList.add('is-visible');
      peekVisible = true;
    }

    function isModifierKey(key) {
      return key === 'Alt' || key === 'AltGraph' || key === 'Shift' || key === 'Meta' || key === 'Control';
    }

    function dismissShortcutsHint() {
      clearShortcutsHintCountdown();
      clearShortcutsHintAnnouncement();
      var wasKeyboardNavFlow = hintTriggeredByKeyboardNav;
      hintDismissed = true;
      hintShown = false;
      hintTriggeredByKeyboardNav = false;
      if (hintBubble) {
        hintBubble.hidden = true;
      }
      try {
        sessionStorage.setItem(hintDismissStorageKey, '1');
      } catch (e) {
        /* ignore */
      }
      // After the hint closes, surface the alt-only peek so keyboard-nav users know
      // they can hold Alt/Option at any time to recall the shortcut list.
      // Only do this when the hint was originally triggered by the tab-four-times
      // keyboard-nav flow (not, e.g., if a mouse user somehow triggered it).
      if (wasKeyboardNavFlow && altPeekBubble) {
        setAltPeekPersistFlag();
        setTimeout(function () {
          if (!altPeekVisible && !peekVisible) {
            showShortcutsAltPeek();
          }
        }, 400);
      }
    }

    function showShortcutsHint() {
      if (hintDismissed || hintShown || !hintBubble) return;
      renderShortcutsHintList();
      hintBubble.hidden = false;
      hintShown = true;
      announceShortcutsHint();
      startShortcutsHintCountdown();
    }

    function bindShortcutsHint() {
      hintBubble = document.getElementById('a11y-shortcuts-hint');
      hintAnnouncer = document.getElementById('a11y-shortcuts-announcer');
      try {
        hintDismissed = sessionStorage.getItem(hintDismissStorageKey) === '1';
      } catch (e) {
        /* ignore */
      }
      if (!hintBubble) return;
      hintList = hintBubble.querySelector('.a11y-shortcuts-hint-list');
      hintCountdownEl = hintBubble.querySelector('.a11y-shortcuts-hint-countdown');
      hintCountdownProgress = hintBubble.querySelector('.a11y-shortcuts-hint-ring-progress');
      hintCountdownSeconds = hintBubble.querySelector('.a11y-shortcuts-hint-countdown-seconds');
      updateHintCountdownDisplay(hintCountdownTotalMs);
      syncShortcutsHintCountdownLabels();

      var a11yTrigger = document.getElementById('a11y-trigger');
      if (a11yTrigger) {
        a11yTrigger.addEventListener('click', dismissShortcutsHint);
      }

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
          hintTabCount++;
          if (hintTabCount >= 4) {
            hintTriggeredByKeyboardNav = true;
            showShortcutsHint();
          }
          return;
        }

        if (event.key === 'Escape' && hintShown) {
          dismissShortcutsHint();
        }
      }, true);

      document.addEventListener('keydown', function (event) {
        if (!hintShown) return;
        var nativeEv = nativeKeyboardEvent(event);
        if (nativeEv.key === 'Alt' || nativeEv.key === 'AltGraph') {
          pauseShortcutsHintCountdown();
        }
      }, true);

      document.addEventListener('keyup', function (event) {
        if (!hintShown) return;
        var nativeEv = nativeKeyboardEvent(event);
        if (nativeEv.key === 'Alt' || nativeEv.key === 'AltGraph') {
          resumeShortcutsHintCountdown();
        }
      }, true);

      window.addEventListener('blur', function () {
        if (hintShown) {
          pauseShortcutsHintCountdown();
        }
      });
    }

    function bindShortcutsPeek() {
      peekBubble = document.getElementById('a11y-shortcuts-peek');
      if (peekBubble) {
        peekList = peekBubble.querySelector('.a11y-shortcuts-peek-list');
        peekHeader = peekBubble.querySelector('.a11y-shortcuts-peek-header');
      }

      altPeekBubble = document.getElementById('a11y-shortcuts-alt-peek');
      if (altPeekBubble) {
        altPeekBody = altPeekBubble.querySelector('.a11y-shortcuts-alt-peek-body');
      }

      if (!peekBubble && !altPeekBubble) return;

      if (altPeekBubble && getAltPeekSessionState() === 'persist' && !altPeekShowTimer) {
        altPeekShowTimer = setTimeout(function () {
          altPeekShowTimer = null;
          if (!altPeekVisible && !peekVisible) {
            showShortcutsAltPeek();
          }
        }, 400);
      }

      document.addEventListener('keydown', function (event) {
        if (event.altKey && event.shiftKey) {
          setAltPeekDismissedFlag();
          clearAltPeekTimer();
          hideShortcutsAltPeek();
        }
        if (!event.altKey || !event.shiftKey) return;
        var nativeEv = nativeKeyboardEvent(event);
        var key = nativeEv.key;
        if (key && !isModifierKey(key)) {
          hideShortcutsPeek();
          return;
        }
        if (!peekVisible && !peekShowTimer) {
          peekShowTimer = setTimeout(function () {
            peekShowTimer = null;
            showShortcutsPeek();
          }, 150);
        }
      }, true);

      document.addEventListener('keydown', function (event) {
        if (event.altKey && event.shiftKey) {
          setAltPeekDismissedFlag();
          clearAltPeekTimer();
          hideShortcutsAltPeek();
        }
        if (!event.altKey || event.shiftKey || event.ctrlKey || event.metaKey) return;
        if (isEditableElement(document.activeElement)) return;
        if (document.visibilityState && document.visibilityState !== 'visible') return;
        var nativeEv = nativeKeyboardEvent(event);
        var key = nativeEv.key;
        if (key && !isModifierKey(key)) {
          clearAltPeekTimer();
          hideShortcutsAltPeek();
          return;
        }
        if (!altPeekVisible && !altPeekShowTimer) {
          altPeekShowTimer = setTimeout(function () {
            altPeekShowTimer = null;
            showShortcutsAltPeek();
          }, 150);
        }
      }, true);

      document.addEventListener('keyup', function (event) {
        var nativeEv = nativeKeyboardEvent(event);
        if (nativeEv.key === 'Alt' || nativeEv.key === 'Shift') {
          hideShortcutsPeek();
        }
        if (nativeEv.key === 'Alt' || nativeEv.key === 'AltGraph') {
          hideShortcutsAltPeek();
        }
      }, true);

      var dismissAltPeekFromMouse = function (event) {
        var state = getAltPeekSessionState();
        if (!altPeekVisible && state !== 'persist') return;
        if (event.type === 'pointerdown' && event.pointerType && event.pointerType !== 'mouse') return;
        setAltPeekDismissedFlag();
        hideShortcutsAltPeek();
      };
      if (window.PointerEvent) {
        document.addEventListener('pointerdown', dismissAltPeekFromMouse, true);
      } else {
        document.addEventListener('mousedown', dismissAltPeekFromMouse, true);
      }

      window.addEventListener('blur', function () {
        // Always dismiss the Alt+Shift chord peek on blur — it requires both modifiers
        // to be held, and if the window loses focus those modifier states are unreliable.
        hideShortcutsPeek();
        // Do NOT dismiss the alt-only peek on blur. On Firefox/Windows, pressing Alt
        // briefly moves focus to the browser menu bar, firing window.blur before the
        // 150 ms show-timer fires. Cancelling the timer here means the peek never
        // appears on Firefox/Windows at all. Instead, we rely on the keyup listener
        // (which fires even while the Firefox menu bar is active) and visibilitychange
        // (which fires on genuine tab/window switches) to dismiss the alt-only peek.
      });
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible') {
          hideShortcutsPeek();
          hideShortcutsAltPeek();
        }
      });
    }

    /** Alt+Shift + physical letter (Option+Shift on Mac). Prefer KeyboardEvent.code; fall back to keyCode for old engines. */
    function isAltShiftPhysicalLetter(jqEvent, asciiLetter) {
      var ev = nativeKeyboardEvent(jqEvent);
      var upper = String(asciiLetter || '').toUpperCase();
      var code = 'Key' + upper;
      var wantKeyCode = upper.length === 1 ? upper.charCodeAt(0) : 0;
      if (!ev.altKey || !ev.shiftKey || ev.ctrlKey || ev.metaKey) return false;
      if (ev.code === code) return true;
      if (wantKeyCode && (ev.keyCode === wantKeyCode || ev.which === wantKeyCode)) return true;
      return false;
    }

    function isEditableElement(el) {
      if (!el) return false;
      if (el.isContentEditable) return true;

      var tag = (el.tagName || '').toLowerCase();
      if (tag === 'textarea' || tag === 'select') return true;
      if (tag === 'input') {
        var type = (el.type || 'text').toLowerCase();
        return ['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file'].indexOf(type) === -1;
      }
      return false;
    }

    function getFirstFocusable($root) {
      if (!$root || !$root.length) return $();
      return $root
        .find('a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
        .filter(':visible')
        .first();
    }

    function shouldHandleShortcut(event, shortcut) {
      if (!shortcut || typeof shortcut.match !== 'function' || typeof shortcut.run !== 'function') return false;
      if (event.defaultPrevented || event.isComposing || event.repeat) return false;
      if (isEditableElement(document.activeElement)) return false;

      // Preserve focus trapping contexts: don't yank focus back to nav while cart drawer is active.
      if (shortcut.id === 'jump-to-nav' && $('body').hasClass('mobile-no-scroll')) return false;

      return shortcut.match(event) === true;
    }

    function onKeydown(event) {
      var nativeEv = nativeKeyboardEvent(event);
      if (shortcutsDebugEnabled() && nativeEv.altKey && nativeEv.shiftKey && !nativeEv.ctrlKey && !nativeEv.metaKey) {
        if (
          nativeEv.code === 'KeyA' || nativeEv.code === 'KeyN' || nativeEv.code === 'KeyC' || nativeEv.code === 'KeyS' || nativeEv.code === 'KeyX' ||
          nativeEv.keyCode === 65 || nativeEv.keyCode === 78 || nativeEv.keyCode === 67 || nativeEv.keyCode === 83 || nativeEv.keyCode === 88
        ) {
          debugLog('keydown chord', {
            shortcutIdGuess:
              (nativeEv.code === 'KeyA' || nativeEv.keyCode === 65) ? 'open-a11y-panel?' :
              (nativeEv.code === 'KeyN' || nativeEv.keyCode === 78) ? 'jump-to-nav?' :
              (nativeEv.code === 'KeyX' || nativeEv.keyCode === 88) ? 'close-shortcuts-hint?' :
              (nativeEv.code === 'KeyS' || nativeEv.keyCode === 83) ? 'open-search?' :
              'open-cart?',
            jqCode: event.code,
            nativeCode: nativeEv.code,
            key: nativeEv.key,
            keyCode: nativeEv.keyCode,
            which: nativeEv.which,
            activeTag: document.activeElement && document.activeElement.tagName
          });
        }
      }

      for (var i = 0; i < shortcuts.length; i++) {
        var shortcut = shortcuts[i];
        var matched = false;
        try {
          matched = shouldHandleShortcut(event, shortcut);
        } catch (err) {
          matched = false;
        }

        if (!matched) continue;

        event.preventDefault();
        event.stopPropagation();
        debugLog('triggered', shortcut.id);
        try {
          shortcut.run(event);
          dismissShortcutsHint();
          hideShortcutsPeek();
          clearAltPeekTimer();
          hideShortcutsAltPeek();
        } catch (runErr) {
          debugLog('run() error', shortcut.id, runErr);
          // Do not break other shortcuts if one handler fails.
        }
        return;
      }
    }

    function registerShortcut(definition) {
      if (!definition || !definition.id) return;
      unregisterShortcut(definition.id);
      shortcuts.push(definition);
    }

    function unregisterShortcut(id) {
      shortcuts = shortcuts.filter(function (item) {
        return item.id !== id;
      });
    }

    window.AbstraxShortcuts = {
      register: registerShortcut,
      unregister: unregisterShortcut,
      list: function () {
        return shortcuts.slice();
      }
    };

    $(document)
      .off('keydown.abstraxShortcuts')
      .on('keydown.abstraxShortcuts', onKeydown);

    registerShortcut({
      id: 'jump-to-nav',
      description: 'Jump to primary navigation',
      keyLabel: 'Alt+Shift+N',
      triggerKey: 'N',
      shortLabel: 'Navigation',
      match: function (event) {
        return isAltShiftPhysicalLetter(event, 'N');
      },
      run: function () {
        var $primaryNav = $('nav.main-menu[aria-label="Primary"]').first();
        if (!$primaryNav.length) {
          $primaryNav = $('nav.main-menu').first();
        }

        var $target = getFirstFocusable($primaryNav);
        if (!$target.length) {
          $target = $('.nav-toggle').filter(':visible').first();
        }

        if ($target.length) {
          $target.focus();
        }
      }
    });

    registerShortcut({
      id: 'open-cart',
      description: 'Open cart',
      keyLabel: 'Alt+Shift+C',
      triggerKey: 'C',
      shortLabel: 'Cart',
      match: function (event) {
        return isAltShiftPhysicalLetter(event, 'C');
      },
      run: function () {
        var selectors = ['.cart-icon:visible', '#cart2:visible', '#cart:visible', '#cart4:visible'];
        var $trigger = $();

        for (var i = 0; i < selectors.length; i++) {
          $trigger = $(selectors[i]).first();
          if ($trigger.length) break;
        }

        if (!$trigger.length) return;

        $trigger.trigger('click');

        setTimeout(function () {
          var $drawer = $('.side-cart').first();
          var $drawerTarget = getFirstFocusable($drawer);
          if ($drawerTarget.length) {
            $drawerTarget.focus();
          }
        }, 150);
      }
    });

    registerShortcut({
      id: 'open-a11y-panel',
      description: 'Open accessibility settings',
      keyLabel: 'Alt+Shift+A',
      triggerKey: 'A',
      shortLabel: 'Accessibility',
      match: function (event) {
        return isAltShiftPhysicalLetter(event, 'A');
      },
      run: function () {
        var $trigger = $('#a11y-trigger').first();
        if (!$trigger.length) return;
        $trigger.trigger('click');
        $trigger.focus();
      }
    });

    registerShortcut({
      id: 'open-search',
      description: 'Open search',
      keyLabel: 'Alt+Shift+S',
      triggerKey: 'S',
      shortLabel: 'Search',
      match: function (event) {
        return isAltShiftPhysicalLetter(event, 'S');
      },
      run: function () {
        var $icon = $('.search-icon:visible').first();
        if ($icon.length) {
          $icon.trigger('click');
        } else {
          var $delegated = $('[data-action="open-search"]:visible, [data-action="toggle-search"]:visible').first();
          if ($delegated.length) {
            $delegated.trigger('click');
          } else {
            return;
          }
        }
        setTimeout(function () {
          var $q = $('#Search .Search__Input, #Search [name="q"], .desk-search_wrap .Search__Input').filter(':visible').first();
          if ($q.length) {
            $q.focus();
          }
        }, 520);
      }
    });

    registerShortcut({
      id: 'close-shortcuts-hint',
      description: 'Close this keyboard shortcuts popup',
      keyLabel: 'Alt+Shift+X',
      triggerKey: 'X',
      shortLabel: 'Close this popup',
      match: function (event) {
        return isAltShiftPhysicalLetter(event, 'X');
      },
      run: function () {
        dismissShortcutsHint();
      }
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        bindShortcutsHint();
        bindShortcutsPeek();
      });
    } else {
      bindShortcutsHint();
      bindShortcutsPeek();
    }
    window.AbstraxShortcutsHint = {
      dismiss: dismissShortcutsHint
    };
  })(window.jQuery);
})();
